import { useEffect, useRef, useState } from 'react'

import { Icon, TextButton, typography } from '@joor/design-system'
import styled from 'styled-components'

import { ViewModeIds } from 'features/LeadGen/RetailerProfile/retailerProfilePage.ids'

const BRANDS_COLLAPSED_LIMIT = 20

const Card = styled.section`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--color-primary-300);
  border-radius: 10px;
  background-color: #fff;
  overflow: hidden;
`

const TitleBar = styled.div`
  background-color: var(--color-secondary-100);
  border-bottom: 1px solid var(--color-primary-200);
  padding: 14px 20px;
`

const Title = styled.span`
  margin: 0;
  ${typography.alpha.primaryActive.headline3}
  color: var(--color-primary-700);
`

const Columns = styled.div`
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media only screen and (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0;
    align-items: stretch;
  }
`

const Column = styled.div`
  min-width: 0;

  @media only screen and (min-width: 768px) {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0 20px;
    box-sizing: border-box;

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      padding-right: 0;
    }

    &:not(:last-child) {
      border-right: 1px solid var(--color-primary-200);
    }
  }

  @media only screen and (max-width: 767px) {
    &:not(:last-child) {
      padding-bottom: 20px;
      border-bottom: 1px solid var(--color-primary-200);
    }
  }
`

const SectionLabel = styled.div`
  ${typography.alpha.primaryActive.body3}
  font-size: var(--size-font-small);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  letter-spacing: 0.08em;
  color: var(--color-primary-500);
  margin-bottom: 12px;
`

const BadgeList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const Badge = styled.li`
  ${typography.alpha.primaryActive.subtitle3}
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  box-sizing: border-box;
  background-color: var(--color-secondary-300);
  max-width: 100%;
  word-break: break-word;
`

const BrandsExpandFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
`

const ExpandControlInner = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

type JoorActivityInsightsWidgetProps = {
  /** Calendar years when the retailer placed orders on JOOR (from retailer profile). */
  yearsOrderedOnJoor: string[]
  brandsPurchasedOnJoor: string[]
  /** Not yet on retailer profile; pass [] until API exposes categories. */
  categoriesPurchasedOnJoor: string[]
}

const JoorActivityInsightsWidget = ({
  yearsOrderedOnJoor,
  brandsPurchasedOnJoor,
  categoriesPurchasedOnJoor,
}: JoorActivityInsightsWidgetProps) => {
  const [brandsExpanded, setBrandsExpanded] = useState(false)
  const cardRef = useRef<HTMLElement>(null)
  const scrollIntoViewAfterCollapseRef = useRef(false)
  const brandCount = brandsPurchasedOnJoor.length
  const hasMoreBrands = brandCount > BRANDS_COLLAPSED_LIMIT
  const showCollapsedBrands = hasMoreBrands && !brandsExpanded
  const visibleBrands = showCollapsedBrands
    ? brandsPurchasedOnJoor.slice(0, BRANDS_COLLAPSED_LIMIT)
    : brandsPurchasedOnJoor
  const moreBrandsCount = brandCount - BRANDS_COLLAPSED_LIMIT

  useEffect(() => {
    if (!brandsExpanded && scrollIntoViewAfterCollapseRef.current) {
      scrollIntoViewAfterCollapseRef.current = false
      cardRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [brandsExpanded])

  const toggleBrandsExpanded = () => {
    setBrandsExpanded((expanded) => {
      if (expanded) {
        scrollIntoViewAfterCollapseRef.current = true
      }
      return !expanded
    })
  }

  return (
    <Card
      ref={cardRef}
      data-testid={ViewModeIds.JoorActivityInsights.Container}
    >
      <TitleBar>
        <Title>JOOR Activity & Insights</Title>
      </TitleBar>
      <Columns>
        <Column>
          <SectionLabel>Years Ordered on JOOR</SectionLabel>
          <BadgeList>
            {yearsOrderedOnJoor.map((label, index) => (
              <Badge key={`y-${index}-${label}`} className="notranslate">
                {label}
              </Badge>
            ))}
          </BadgeList>
        </Column>
        <Column>
          <SectionLabel>Categories Purchased on JOOR</SectionLabel>
          <BadgeList>
            {categoriesPurchasedOnJoor.map((label, index) => (
              <Badge key={`c-${index}-${label}`} className="notranslate">
                {label}
              </Badge>
            ))}
          </BadgeList>
        </Column>
        <Column>
          <SectionLabel>Brands Purchased on JOOR</SectionLabel>
          <BadgeList>
            {visibleBrands.map((label, index) => (
              <Badge key={`b-${index}-${label}`} className="notranslate">
                {label}
              </Badge>
            ))}
            {showCollapsedBrands && moreBrandsCount > 0 && (
              <Badge key="brands-more-count" className="notranslate">
                +{moreBrandsCount} more
              </Badge>
            )}
          </BadgeList>
          {hasMoreBrands && (
            <BrandsExpandFooter>
              <TextButton type="button" onClick={toggleBrandsExpanded}>
                <ExpandControlInner>
                  <Icon
                    iconName={brandsExpanded ? 'contract' : 'expand'}
                    iconSize="small"
                    variant="ACTIVE"
                    plain
                  />
                  {brandsExpanded ? 'Expand Less' : 'Expand All'}
                </ExpandControlInner>
              </TextButton>
            </BrandsExpandFooter>
          )}
        </Column>
      </Columns>
    </Card>
  )
}

export default JoorActivityInsightsWidget
